import { useCallback, useState, RefObject } from "react";
import { exportToJson, exportToPng, exportToPdf } from "../utils/exportUtils";

export type UseExportHandlersOptions = {
  beforeExport?: () => void | Promise<void>;
  afterExport?: () => void | Promise<void>;
};

export const useExportHandlers = (
  containerRef: RefObject<HTMLDivElement>,
  data: unknown,
  options: UseExportHandlersOptions = {},
) => {
  const [isExporting, setIsExporting] = useState(false);
  const { beforeExport, afterExport } = options;

  const runImageExport = useCallback(
    async (exportFn: (ref: RefObject<HTMLDivElement>) => Promise<void>) => {
      if (isExporting) return;
      setIsExporting(true);
      try {
        await beforeExport?.();
        await exportFn(containerRef);
      } finally {
        try {
          await afterExport?.();
        } finally {
          setIsExporting(false);
        }
      }
    },
    [afterExport, beforeExport, containerRef, isExporting],
  );

  const handleExportJson = useCallback(() => {
    exportToJson(data as Parameters<typeof exportToJson>[0]);
  }, [data]);

  const handleExportPng = useCallback(async () => {
    await runImageExport(exportToPng);
  }, [runImageExport]);

  const handleExportPdf = useCallback(async () => {
    await runImageExport(exportToPdf);
  }, [runImageExport]);

  return {
    isExporting,
    handleExportJson,
    handleExportPng,
    handleExportPdf,
  };
};
